import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
} from 'react-icons/fa';
import { HiOutlineMail, HiOutlinePhone } from 'react-icons/hi';
import { FOOTER, SITE, CONTACT_INFO, SOCIAL_LINKS } from '../config/content';
import logoWhite from '../assets/images/logo_white.png';
import MarketSwitcher from './MarketSwitcher';
import styles from './Footer.module.css';

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  Facebook: <FaFacebookF />,
  Instagram: <FaInstagram />,
  Twitter: <FaTwitter />,
  LinkedIn: <FaLinkedinIn />,
};

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Newsletter signup logic
    setEmail('');
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.topDivider} />

      {/* ── Main grid ── */}
      <div className={styles.footerContent}>
        {/* Brand */}
        <div className={styles.brandCol}>
          <div className={styles.logo}>
            <img src={logoWhite} alt="JurisTab" className={styles.logoIcon} />
          </div>
          <p className={styles.tagline}>{FOOTER.tagline}</p>
          <div className={styles.socialLinks}>
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={s.label}
              >
                {SOCIAL_ICONS[s.label]}
              </a>
            ))}
          </div>
        </div>

        {/* Products */}
        <div className={styles.linkCol}>
          <h4 className={styles.colHeading}>{FOOTER.productsHeading}</h4>
          <div className={styles.linkList}>
            {FOOTER.products.map((p) => (
              <a
                key={p.label}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkItem}
              >
                {p.label}
              </a>
            ))}
          </div>
        </div>

        {/* Company */}
        <div className={styles.linkCol}>
          <h4 className={styles.colHeading}>{FOOTER.companyHeading}</h4>
          <div className={styles.linkList}>
            {FOOTER.companyLinks.map((link) => (
              <Link key={link.label} to={link.href} className={styles.linkItem}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Support */}
        <div className={styles.linkCol}>
          <h4 className={styles.colHeading}>{FOOTER.supportHeading}</h4>
          <div className={styles.linkList}>
            {FOOTER.supportLinks.map((link) => (
              <Link key={link.label} to={link.href} className={styles.linkItem}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Newsletter */}
        <div className={styles.newsletterCol}>
          <h4 className={styles.colHeading}>{FOOTER.newsletter.heading}</h4>
          <p className={styles.newsletterText}>
            {FOOTER.newsletter.text}
          </p>
          <form className={styles.newsletterForm} onSubmit={handleSubmit}>
            <div className={styles.inputWrapper}>
              <input
                type="email"
                placeholder={FOOTER.newsletter.placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.emailInput}
                required
              />
            </div>
            <button type="submit" className={styles.submitBtn}>
              {FOOTER.newsletter.button}
            </button>
          </form>
        </div>
      </div>

      {/* ── Contact row ── */}
      <div className={styles.contactRow}>
        <a href={CONTACT_INFO.emailHref} className={styles.contactItem}>
          <HiOutlineMail className={styles.contactIcon} />
          {CONTACT_INFO.email}
        </a>
        {CONTACT_INFO.phone && (
          <a href={CONTACT_INFO.phoneHref} className={styles.contactItem}>
            <HiOutlinePhone className={styles.contactIcon} />
            {CONTACT_INFO.phone}
          </a>
        )}
      </div>

      {/* ── Bottom bar ── */}
      <div className={styles.bottomBar}>
        <div className={styles.bottomContent}>
          <span className={styles.copyright}>
            {SITE.copyright}
          </span>
          <MarketSwitcher />
          <div className={styles.bottomLinks}>
            {FOOTER.bottomLinks.map((link) => (
              <Link key={link.label} to={link.href} className={styles.bottomLink}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
